import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ApiClient } from "../api/client";
import type {
  ActivityCategory,
  ActivityStatus,
  AdminActivityItem,
} from "../api/types";
import { formatDateTime, formatUsdString } from "../format";
import { useCompact } from "../hooks";
import { ErrorNotice, Loading } from "./Notice";
import { Section } from "./Section";

/**
 * The seven categories the API serves, in the order the app's filter shows
 * them. The wire values are the labels' lowercase; a new category on the API
 * is a compile error on `ActivityCategory` before it is a missing chip here.
 */
const CATEGORIES: { value: ActivityCategory; label: string }[] = [
  { value: "deposit", label: "Deposit" },
  { value: "withdraw", label: "Withdraw" },
  { value: "transfer", label: "Transfer" },
  { value: "spend", label: "Spend" },
  { value: "fee", label: "Fee" },
  { value: "earn", label: "Earn" },
  { value: "swap", label: "Swap" },
];

const STATUS_CHIP: Record<ActivityStatus, string> = {
  completed: "chip chip-ok",
  pending: "chip chip-neutral",
  processing: "chip chip-neutral",
  failed: "chip chip-bad",
  canceled: "chip chip-muted",
};

/**
 * A member's activity feed: the app's own rows, read on their behalf.
 *
 * Paging follows `Referrals.tsx`: one generation counter invalidates both the
 * first-page effect and a `loadMore` in flight, so switching category while a
 * page is loading drops that page on arrival instead of appending rows from
 * the old filter and installing a cursor that belongs to it.
 */
export function ActivityPanel({ api, userId }: { api: ApiClient; userId: string }) {
  const compact = useCompact();
  const [category, setCategory] = useState<ActivityCategory | null>(null);
  const [items, setItems] = useState<AdminActivityItem[] | null>(null);
  const [spent, setSpent] = useState<{ label: string; amountUsd: string } | null>(null);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [error, setError] = useState<unknown>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const generation = useRef(0);

  useEffect(() => {
    const current = generation.current;
    setItems(null);
    setError(null);
    setNextCursor(undefined);
    api
      .memberActivity(userId, category ? { category } : {})
      .then((page) => {
        if (current !== generation.current) return;
        setItems(page.items);
        setSpent(page.spentThisPeriod ?? null);
        setNextCursor(page.nextCursor);
      })
      .catch((err: unknown) => {
        if (current === generation.current) setError(err);
      });
    return () => {
      generation.current += 1;
    };
  }, [api, userId, category]);

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    const current = generation.current;
    setLoadingMore(true);
    try {
      const page = await api.memberActivity(userId, {
        cursor: nextCursor,
        ...(category ? { category } : {}),
      });
      if (current !== generation.current) return;
      setItems((prev) => [...(prev ?? []), ...page.items]);
      setNextCursor(page.nextCursor);
    } catch (err) {
      if (current === generation.current) setError(err);
    } finally {
      setLoadingMore(false);
    }
  }, [api, userId, nextCursor, loadingMore, category]);

  return (
    <Section
      title="Activity"
      aside={
        spent ? (
          <span className="subtle">
            {spent.label}: {formatUsdString(spent.amountUsd)}
          </span>
        ) : null
      }
    >
      <div className="panel-body panel-body-flush">
        <CategoryPicker value={category} onChange={setCategory} />
      </div>

      {error ? (
        <div className="panel-body">
          <ErrorNotice error={error} />
        </div>
      ) : items === null ? (
        <Loading what="activity" />
      ) : items.length === 0 ? (
        <div className="empty">
          <strong>No activity{category ? " in this category" : ""}</strong>
          <p className="subtle">
            {category
              ? "Try another category, or clear the filter."
              : "Nothing has happened on this account yet."}
          </p>
        </div>
      ) : compact ? (
        <ActivityList items={items} />
      ) : (
        <ActivityTable items={items} />
      )}

      {nextCursor ? (
        <div className="panel-foot">
          <button type="button" disabled={loadingMore} onClick={() => void loadMore()}>
            {loadingMore ? "Loading..." : "Load more"}
          </button>
        </div>
      ) : null}
    </Section>
  );
}

/**
 * On a phone the eight segments are wider than the screen, and the panel's
 * clip cut off everything after Transfer. There they scroll sideways instead,
 * with the edge faded so the row reads as continuing, and the chosen one is
 * brought into view.
 */
function CategoryPicker({
  value,
  onChange,
}: {
  value: ActivityCategory | null;
  onChange: (next: ActivityCategory | null) => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const chosen = ref.current?.querySelector<HTMLElement>("[aria-pressed='true']");
    chosen?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  }, [value]);

  return (
    <div className="segmented segmented-scroll" role="group" aria-label="Activity category" ref={ref}>
      <button
        type="button"
        className={value === null ? "selected" : ""}
        aria-pressed={value === null}
        onClick={() => onChange(null)}
      >
        All
      </button>
      {CATEGORIES.map((c) => (
        <button
          key={c.value}
          type="button"
          className={value === c.value ? "selected" : ""}
          aria-pressed={value === c.value}
          onClick={() => onChange(c.value)}
        >
          {c.label}
        </button>
      ))}
    </div>
  );
}

function ActivityTable({ items }: { items: AdminActivityItem[] }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>When</th>
            <th>What</th>
            <th>Category</th>
            <th>Status</th>
            <th className="num">Amount</th>
            <th className="num">USD</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id}>
              <td className="num">{formatDateTime(item.occurredAt)}</td>
              <td>
                <div className="cell-strong">{item.title}</div>
                <div className="subtle">{item.subtitle}</div>
              </td>
              <td>{item.category}</td>
              <td>
                <span className={STATUS_CHIP[item.status] ?? "chip chip-neutral"}>
                  {item.status}
                </span>
              </td>
              {/*
                Coloured by direction, not by sign: a Transfer between the
                member's own surfaces is neither money arriving nor leaving,
                the same rule the app's feed follows.
              */}
              <td className={`num amount-${item.direction}`}>{item.amountDisplay}</td>
              <td className={`num amount-${item.direction}`}>
                {formatUsdString(item.amountUsd)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The feed as a phone's list: what and when on the left, the money on the right. */
function ActivityList({ items }: { items: AdminActivityItem[] }) {
  return (
    <ul className="list">
      {items.map((item) => (
        <li key={item.id} className="list-row">
          <span className="list-main">
            <span className="list-title">
              <span className="list-title-text">{item.title}</span>
            </span>
            <span className="list-meta">
              <span>{item.subtitle}</span>
            </span>
            <span className="list-meta">
              <span>{formatDateTime(item.occurredAt)}</span>
              {item.status === "completed" ? null : (
                <span className={STATUS_CHIP[item.status] ?? "chip chip-neutral"}>
                  {item.status}
                </span>
              )}
            </span>
          </span>
          <span className={`list-trail list-amount amount-${item.direction}`}>
            <span>{item.amountDisplay}</span>
            <span className="subtle">{formatUsdString(item.amountUsd)}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}
